/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { IconViewProps } from '@airasia-phoenix/react-native/components/IconView';
import ListView from '@airasia-phoenix/react-native/components/ListView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { pd } from '@airasia-phoenix/react-native/utils';
import { getColor } from '@airasia-phoenix/react-native/utils/utils';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import styles from './styles';

const ICON_SIZE = pd.ref.space.spacing50.px;

const secondaryIconProps = (icon: IconViewProps['icon']) => ({
  icon,
  tint: getColor(pd.sys.colour.content_secondary),
  width: ICON_SIZE,
  height: ICON_SIZE,
});

export interface AlternativeSignInOption {
  key: string;
  label: string;
  icon: IconViewProps['icon'];
  onPress: () => void;
}

export interface AlternativeSignInOptionsViewProps {
  onSignInWithPassword: () => void;
  onSignInWithPhoneNumber: () => void;
  onSignInWithPasskey: () => void;
  testID?: string;
}

export function AlternativeSignInOptionsView({
  onSignInWithPassword,
  onSignInWithPhoneNumber,
  onSignInWithPasskey,
  testID,
}: AlternativeSignInOptionsViewProps) {
  const { t } = useTranslation('account');

  const heading: string = t('verifyEmailPage.tryAnotherSignInMethod');
  const description: string = t('verifyEmailPage.alternativeSignInDescription');

  const options: AlternativeSignInOption[] = [
    {
      key: 'phone-number',
      label: t('verifyEmailPage.signInWithPhoneNumber'),
      icon: pd.icon.main.hardware.smartphone,
      onPress: onSignInWithPhoneNumber,
    },
    {
      key: 'passkey',
      label: t('verifyEmailPage.signInWithPasskey'),
      icon: pd.icon.main.communication.vpn_key,
      onPress: onSignInWithPasskey,
    },
    {
      key: 'email-password',
      label: t('verifyEmailPage.signInWithPassword'),
      icon: pd.icon.main.actions.lock,
      onPress: onSignInWithPassword,
    },
  ];

  return (
    <View style={styles.container}>
      <View style={styles.separator} />
      <TextView
        style={styles.heading}
        text={heading}
        testID={
          testID ? `${testID}-Heading` : 'AlternativeSignInOptionsView-Heading'
        }
      />

      <View style={styles.optionsContainer}>
        {options.map(({ key, icon, label, onPress }) => (
          <ListView
            key={key}
            primaryText={label}
            startElement={{
              iconViewProps: secondaryIconProps(icon),
              alignSelf: 'center',
            }}
            primaryTextStyle={styles.optionLabel}
            endElement={{
              iconViewProps: secondaryIconProps(
                pd.icon.main.navigation.chevron_right,
              ),
              alignSelf: 'center',
              containerStyle: styles.endElement,
            }}
            containerStyle={styles.listContent}
            onPress={onPress}
          />
        ))}

        <View style={styles.descriptionContainer}>
          <TextView style={styles.description} text={description} />
        </View>
      </View>
    </View>
  );
}
